import {
	addDays,
	addMonths,
	addQuarters,
	addWeeks,
	addYears,
	differenceInCalendarDays,
	endOfDay,
	endOfMonth,
	endOfQuarter,
	endOfWeek,
	endOfYear,
	isSameDay,
	startOfDay,
	startOfMonth,
	startOfQuarter,
	startOfWeek,
	startOfYear,
	subDays,
	subMonths,
	subQuarters,
	subWeeks,
	subYears,
} from "date-fns/esm";

export type CalendarRangeUnit = "year" | "quarter" | "month" | "week";

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

/** Detect if from/to match a full calendar year, quarter, month, or Mon-Sun week. */
export function detectCalendarRangeUnit(from: Date, to: Date): CalendarRangeUnit | null {
	const start = startOfDay(from);
	const end = startOfDay(to);

	if (isSameDay(start, startOfYear(start)) && isSameDay(end, startOfDay(endOfYear(start)))) {
		return "year";
	}
	if (isSameDay(start, startOfQuarter(start)) && isSameDay(end, startOfDay(endOfQuarter(start)))) {
		return "quarter";
	}
	if (isSameDay(start, startOfMonth(start)) && isSameDay(end, startOfDay(endOfMonth(start)))) {
		return "month";
	}
	if (
		isSameDay(start, startOfWeek(start, WEEK_OPTIONS)) &&
		isSameDay(end, startOfDay(endOfWeek(start, WEEK_OPTIONS)))
	) {
		return "week";
	}
	return null;
}

function shiftAnchoredUnit(from: Date, direction: -1 | 1, unit: CalendarRangeUnit): { from: Date; to: Date } {
	const anchor =
		unit === "year"
			? direction < 0
				? subYears(from, 1)
				: addYears(from, 1)
			: unit === "quarter"
				? direction < 0
					? subQuarters(from, 1)
					: addQuarters(from, 1)
				: unit === "month"
					? direction < 0
						? subMonths(from, 1)
						: addMonths(from, 1)
					: direction < 0
						? subWeeks(from, 1)
						: addWeeks(from, 1);

	switch (unit) {
		case "year":
			return { from: startOfDay(startOfYear(anchor)), to: endOfDay(endOfYear(anchor)) };
		case "quarter":
			return { from: startOfDay(startOfQuarter(anchor)), to: endOfDay(endOfQuarter(anchor)) };
		case "month":
			return { from: startOfDay(startOfMonth(anchor)), to: endOfDay(endOfMonth(anchor)) };
		case "week":
			return {
				from: startOfDay(startOfWeek(anchor, WEEK_OPTIONS)),
				to: endOfDay(endOfWeek(anchor, WEEK_OPTIONS)),
			};
	}
}

/** Shift a custom (no rangeType) window: calendar unit when aligned, else inclusive day length. */
export function shiftCustomTimeRange(from: Date, to: Date, direction: -1 | 1): { from: Date; to: Date } {
	const unit = detectCalendarRangeUnit(from, to);
	if (unit) return shiftAnchoredUnit(from, direction, unit);

	const days = Math.max(differenceInCalendarDays(to, from) + 1, 1);
	return {
		from: direction < 0 ? subDays(from, days) : addDays(from, days),
		to: direction < 0 ? subDays(to, days) : addDays(to, days),
	};
}
